/**
 * Calendar card — shows a room's calendar-link state and starts a connect. It is
 * a secondary fetch, resolving after the rest of the page. In dev the provider
 * (Cronofy) is stubbed, so the returned authorization URL is an honest stub — we
 * surface it as-is rather than faking "linked".
 */

import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Button from '@8x8/oxygen-button';
import { Spinner } from '@8x8/oxygen-loaders';
import Tag from '@8x8/oxygen-tag';
import TextField from '@8x8/oxygen-text-field';
import TextLink from '@8x8/oxygen-text-link';
import type { AccessibleCalendar, CalendarLink, CalendarLinkStatus, CalendarProvider } from '@spaces/contracts';
import {
    getCalendarLink,
    getOrgCalendarStatus,
    listAccessibleCalendars,
    setRoomCalendar,
    unlinkRoomCalendar,
    type OrgCalendarStatus
} from '../../api';
import { navigate } from '../../router';
import { fmtTime } from '../../ui/format';
import { useToast } from '../../toast';
import { Card, CardTitle, Skel } from '../../ui/primitives';

// A normal single-column card (a regular grid cell, like the other room-detail cards): status
// facts, then the (optional) org hint, then the room-calendar action — stacked with dividers.
const Body = styled.div`
    display: flex;
    flex-direction: column;
`;

// A definition list of the link's facts — evenly spaced, label muted, value emphasised.
const Facts = styled.dl`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 12px 16px;
    margin: 0;
    font-size: 13px;
`;

const Key = styled.dt`
    color: ${p => p.theme.textColor05};
`;

const Value = styled.dd`
    margin: 0;
    text-align: right;
    color: ${p => p.theme.textColor01};
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// A block under the facts, separated by a hairline — the org hint and the room-calendar action.
const Section = styled.div`
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid ${p => p.theme.ui01};
`;

const SectionLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${p => p.theme.textColor01};
    margin-bottom: 4px;
`;

const Hint = styled.p`
    font-size: 12px;
    line-height: 17px;
    color: ${p => p.theme.textColor05};
    margin: 0 0 12px;
`;

// A compact, in-card org-consent notice (not a full-bleed banner): a warning-tinted strip with a
// dot, the message, and the inline "connect" link — sits inside the card's rhythm.
const OrgHint = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 6px;
    background: ${p => p.theme.ui17};
    border: 1px solid ${p => `${p.theme.warning03}44`};
    font-size: 12px;
    line-height: 17px;
    color: ${p => p.theme.textColor01};
`;

const OrgDot = styled.span`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 5px;
    background: ${p => p.theme.warning03};
`;

const CalRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 9px 0;
    font-size: 13px;
    border-bottom: 1px solid ${p => p.theme.ui01};

    &:last-child {
        border-bottom: none;
    }
`;

const CalMeta = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const CalName = styled.span`
    font-weight: 500;
    color: ${p => p.theme.textColor01};
`;

const CalEmail = styled.span`
    color: ${p => p.theme.textColor05};
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const EmptyHint = styled.p`
    font-size: 12px;
    color: ${p => p.theme.textColor05};
    margin: 12px 0 0;
`;

// The manual bind-by-email row: an address field + a Bind button, side by side. Lets an admin link a
// room by its calendar address without waiting for org room-discovery setup (Places / room list).
const ManualRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 8px;
    margin-top: 12px;
`;

const ManualField = styled.div`
    flex: 1;
    min-width: 0;
`;

// A fixed-height scroll area so a large room list never pushes the manual field off-screen —
// roughly three rows tall, then scrolls.
const RoomList = styled.div`
    margin-top: 12px;
    max-height: 168px;
    overflow-y: auto;
    border: 1px solid ${p => p.theme.ui01};
    border-radius: 6px;
`;

const ListMeta = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
    margin-top: 12px;
    font-size: 12px;
    color: ${p => p.theme.textColor05};
`;

// The bound-state summary: the currently-linked room + an Unlink action, shown instead of the
// picker when a room is already bound (the picker is revealed only on "Change").
const BoundRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid ${p => p.theme.ui01};
    border-radius: 6px;
    background: ${p => p.theme.ui16};
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
`;

const STATE_VARIANT: Record<CalendarLinkStatus, 'light' | 'yellow' | 'blue' | 'red'> = {
    unlinked: 'light',
    pending: 'yellow',
    linked: 'blue',
    error: 'red'
};

// The Microsoft Places admin portal, where an admin sets up rooms so they auto-list (no PowerShell).
const MS_PLACES_PORTAL = 'https://places.cloud.microsoft';

// Map the org enrollment's provider to the calendar-link provider. The org status reports
// 'google' | 'microsoft'; the link record (and calendar_links CHECK) uses 'outlook' for Microsoft.
// Absent/unknown falls back to 'google' (resource calendars are Google by default).
function bindProvider(orgProvider: OrgCalendarStatus['provider'] | undefined): CalendarProvider {
    return orgProvider === 'microsoft' ? 'outlook' : 'google';
}

export function CalendarCard({ roomId }: { roomId: string }): JSX.Element {
    const toast = useToast();
    const [link, setLink] = useState<CalendarLink | null | undefined>(undefined); // undefined = loading
    // Resource-calendar picker: undefined = not loaded, [] = loaded-empty, list = choices.
    const [accessible, setAccessible] = useState<AccessibleCalendar[] | undefined>(undefined);
    const [loadingAccessible, setLoadingAccessible] = useState(false);
    const [binding, setBinding] = useState<string | null>(null);
    const [unbinding, setUnbinding] = useState(false);
    // Manual bind-by-email: the typed room address + its own busy flag.
    const [manualEmail, setManualEmail] = useState('');
    const [manualBinding, setManualBinding] = useState(false);
    // Room-list filter (searchable list) + whether the picker is revealed. When a room is already
    // bound the picker stays hidden behind a "Change" action; showPicker opens it.
    const [filter, setFilter] = useState('');
    const [showPicker, setShowPicker] = useState(false);
    // Org-room delegation state (JIT-15819): undefined = loading; drives the "org not connected"
    // hint. Best-effort — a failed load just hides the hint (never blocks the per-room flow).
    const [orgStatus, setOrgStatus] = useState<OrgCalendarStatus | undefined>(undefined);
    const reqId = useRef(0);

    const loadLink = (): void => {
        const id = ++reqId.current;

        setLink(undefined);
        void getCalendarLink(roomId).then(result => {
            if (id !== reqId.current) {
                return;
            }
            setLink(result.ok ? result.data : null);
        });
    };

    useEffect(() => {
        // Reset per-room UI state on room change (but NOT on a mere refresh — see loadAccessible).
        setAccessible(undefined);
        setShowPicker(false);
        setFilter('');
        loadLink();
        // Org-consent status is customer-level (not per-room), loaded once alongside the link.
        void getOrgCalendarStatus().then(result => {
            if (result.ok) {
                setOrgStatus(result.data);
            }
        });
    }, [roomId]);

    // Load the resource calendars the connected account can read. A "not connected /
    // no grant" backend error is surfaced honestly — we never fake an empty success.
    // A refresh keeps the last list until the new one arrives, so the button label stays
    // "Refresh room list" rather than flickering back to "Choose room calendar".
    const loadAccessible = async (): Promise<void> => {
        setLoadingAccessible(true);
        const result = await listAccessibleCalendars(roomId);

        setLoadingAccessible(false);

        if (!result.ok) {
            setAccessible([]);
            toast.notify('error', `Couldn't list room calendars: ${result.message}`);

            return;
        }

        setAccessible(result.data.calendars);
    };

    // Reveal the picker (bound-state "Change") and load the list.
    const openPicker = (): void => {
        setShowPicker(true);
        void loadAccessible();
    };

    // Bind the room to the chosen resource calendar; refresh the link on success. The provider is
    // the org's enrolled provider (Microsoft rooms bind as 'outlook'), not always Google.
    const bind = async (cal: AccessibleCalendar): Promise<void> => {
        setBinding(cal.email);
        const result = await setRoomCalendar(roomId, cal.email, bindProvider(orgStatus?.provider));

        setBinding(null);

        if (!result.ok) {
            toast.notify('error', `Couldn't bind ${cal.name}: ${result.message}`);

            return;
        }

        toast.notify('success', `Room calendar set to ${cal.name}`);
        loadLink();
    };

    // Bind the room to a manually-entered calendar address — the always-works path that needs no
    // room enumeration (so an admin can link a room before/without org room-discovery setup).
    const bindByEmail = async (): Promise<void> => {
        const email = manualEmail.trim();

        if (!email) {
            toast.notify('error', 'Enter a room calendar address.');

            return;
        }

        setManualBinding(true);
        const result = await setRoomCalendar(roomId, email, bindProvider(orgStatus?.provider));

        setManualBinding(false);

        if (!result.ok) {
            toast.notify('error', `Couldn't bind ${email}: ${result.message}`);

            return;
        }

        toast.notify('success', `Room calendar set to ${email}`);
        setManualEmail('');
        loadLink();
    };

    // Unbind the room's calendar. Confirmed (destructive-ish: the TV/Remote agenda stops updating).
    const unbind = async (): Promise<void> => {
        if (!window.confirm('Unlink this room calendar? The TV and Remote will stop showing its agenda.')) {
            return;
        }

        setUnbinding(true);
        const result = await unlinkRoomCalendar(roomId);

        setUnbinding(false);

        if (!result.ok) {
            toast.notify('error', `Couldn't unlink: ${result.message}`);

            return;
        }

        toast.notify('success', 'Room calendar unlinked.');
        setShowPicker(false);
        loadLink();
    };

    const picked = accessible !== undefined;
    // A room is bound once it has an external calendar id (status linked/pending). When bound, the
    // picker is hidden behind "Change" (showPicker); when unbound, the picker is the headline.
    const isBound = !!link?.externalCalendarId;
    // Provider label for copy (avoid hardcoding "Google" — a Microsoft room binds Outlook).
    const providerLabel
        = orgStatus?.provider === 'microsoft' ? 'Microsoft 365'
            : orgStatus?.provider === 'google' ? 'Google Workspace'
                : '';
    const filtered = (accessible ?? []).filter(cal => {
        const q = filter.trim().toLowerCase();

        return !q || cal.name.toLowerCase().includes(q) || cal.email.toLowerCase().includes(q);
    });

    return (
        <Card>
            <CardTitle>Calendar</CardTitle>

            <Body>
                {link === undefined ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <Skel w="100%" h={14} />
                        <Skel w="70%" h={14} />
                        <Skel w="50%" h={14} />
                    </div>
                ) : (
                    <Facts>
                        <Key>Provider</Key>
                        <Value>{link?.provider ?? '—'}</Value>
                        <Key>State</Key>
                        <Value>
                            <Tag variant={link ? STATE_VARIANT[link.status] : 'light'}>
                                {link?.status ?? 'unlinked'}
                            </Tag>
                        </Value>
                        <Key>Room calendar</Key>
                        <Value title={link?.externalCalendarId ?? undefined}>{link?.externalCalendarId ?? '—'}</Value>
                        <Key>Last synced</Key>
                        <Value>{fmtTime(link?.lastSyncedAt)}</Value>
                    </Facts>
                )}

                {/* Org-consent hint (JIT-15819): shown when the org hasn't granted calendar access. */}
                {orgStatus && !orgStatus.enrolled ? (
                    <Section>
                        <OrgHint>
                            <OrgDot />
                            <span>
                                Org calendar access isn&apos;t connected, so some rooms may not appear below.{' '}
                                <TextLink onClick={() => navigate('/calendars')}>Connect org calendars →</TextLink>
                            </span>
                        </OrgHint>
                    </Section>
                ) : null}

                <Section>
                    <SectionLabel>Room calendar</SectionLabel>
                    <Hint>
                        Bind this room to its{providerLabel ? ` ${providerLabel}` : ''} resource calendar so the TV shows its agenda.
                    </Hint>

                    {isBound && !showPicker ? (
                        // Bound: show the current binding + Unlink; keep the picker hidden behind "Change".
                        <>
                            <BoundRow>
                                <CalMeta>
                                    <CalName>Linked calendar</CalName>
                                    <CalEmail>{link?.externalCalendarId}</CalEmail>
                                </CalMeta>
                                <Button
                                    variant="tertiary"
                                    isDisabled={unbinding}
                                    iconLeft={unbinding ? <Spinner size="small" /> : undefined}
                                    onClick={() => void unbind()}
                                >
                                    Unlink
                                </Button>
                            </BoundRow>
                            <Actions>
                                <TextLink onClick={openPicker}>Change room calendar</TextLink>
                            </Actions>
                        </>
                    ) : (
                        // Unbound (or bound + "Change" clicked): the picker + manual entry.
                        <>
                            <Button
                                variant="primary"
                                isDisabled={loadingAccessible}
                                iconLeft={loadingAccessible ? <Spinner size="small" /> : undefined}
                                onClick={() => void loadAccessible()}
                            >
                                {loadingAccessible ? 'Loading…' : picked ? 'Refresh room list' : 'Choose room calendar'}
                            </Button>

                            {picked ? (
                                accessible.length === 0 ? (
                                    orgStatus?.provider === 'microsoft' ? (
                                        <EmptyHint>
                                            No rooms found. A room mailbox only appears here once it&apos;s set up in{' '}
                                            <TextLink href={MS_PLACES_PORTAL} target="_blank" rel="noreferrer">
                                                Microsoft Places
                                            </TextLink>{' '}
                                            (or added to a room list). You can also bind a room by its address below.
                                        </EmptyHint>
                                    ) : (
                                        <EmptyHint>No room calendars available yet — connect org calendars, or bind a room by its address below.</EmptyHint>
                                    )
                                ) : (
                                    <>
                                        <ListMeta>
                                            <span>{filtered.length} of {accessible.length} room{accessible.length === 1 ? '' : 's'}</span>
                                        </ListMeta>
                                        {accessible.length > 5 ? (
                                            <div style={{ marginTop: 8 }}>
                                                <TextField
                                                    label="Filter rooms"
                                                    value={filter}
                                                    placeholder="Search by name or address"
                                                    fullWidth
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
                                                />
                                            </div>
                                        ) : null}
                                        <RoomList>
                                            {filtered.length === 0 ? (
                                                <EmptyHint style={{ margin: 12 }}>No rooms match “{filter}”.</EmptyHint>
                                            ) : (
                                                filtered.map(cal => {
                                                    const bound = link?.externalCalendarId === cal.email;

                                                    return (
                                                        <CalRow key={cal.email} style={{ padding: '9px 12px' }}>
                                                            <CalMeta>
                                                                <CalName>{cal.name}</CalName>
                                                                <CalEmail>{cal.email}</CalEmail>
                                                            </CalMeta>
                                                            {bound ? (
                                                                <Tag variant="blue">Bound</Tag>
                                                            ) : (
                                                                <Button
                                                                    variant="tertiary"
                                                                    isDisabled={binding !== null}
                                                                    iconLeft={binding === cal.email ? <Spinner size="small" /> : undefined}
                                                                    onClick={() => void bind(cal)}
                                                                >
                                                                    Use
                                                                </Button>
                                                            )}
                                                        </CalRow>
                                                    );
                                                })
                                            )}
                                        </RoomList>
                                    </>
                                )
                            ) : null}

                            {/* Manual bind: always available, no enumeration needed. Works on any tenant, even
                                before room-discovery setup — the admin just types the room's calendar address. */}
                            <ManualRow>
                                <ManualField>
                                    <TextField
                                        label="Or enter a room address"
                                        value={manualEmail}
                                        placeholder="room@yourdomain.com"
                                        fullWidth
                                        isDisabled={manualBinding}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setManualEmail(e.target.value)}
                                    />
                                </ManualField>
                                <Button
                                    variant="secondary"
                                    isDisabled={manualBinding || manualEmail.trim() === ''}
                                    iconLeft={manualBinding ? <Spinner size="small" /> : undefined}
                                    onClick={() => void bindByEmail()}
                                >
                                    Bind
                                </Button>
                            </ManualRow>
                        </>
                    )}
                </Section>
            </Body>
        </Card>
    );
}
